// src/pages/Admin/AdminDashboard/AdminDashboard.jsx
import React, { useState, useEffect, useContext } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { AuthContext } from '../../../context/AuthContext';
import { API_BASE_URL } from '../../../config/api';
import './AdminDashboard.css';
import { 
  FaClock, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaUsers, 
  FaUserShield, 
  FaServer,
  FaArrowRight
} from 'react-icons/fa';

const AdminDashboard = ({ isUnderMaintenance, toggleMaintenanceMode }) => {
  const [data, setData] = useState({
    pendingWhitelist: 0,
    approvedWhitelist: 0,
    suspendedWhitelist: 0,
    registeredUsers: 0,
    definedRoles: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { authToken } = useContext(AuthContext);

  // Estados para el contador de jugadores de FiveM (Opción C)
  const [serverStatus, setServerStatus] = useState({ players: 0, maxPlayers: 64, fivem_maintenance: false });
  const [serverStatusLoading, setServerStatusLoading] = useState(true);

  // Función para verificar conexión con el backend
  const testBackendConnection = async () => {
    try {
      console.log('🔍 Probando conexión con:', `${API_BASE_URL}/testdb`);
      const response = await fetch(`${API_BASE_URL}/testdb`);
      const data = await response.json();
      console.log('✅ Backend responde:', data);
    } catch (err) {
      console.error('❌ Error conectando al backend:', err);
    }
  };

  // Ejecutar test de conexión al cargar
  useEffect(() => {
    testBackendConnection();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Fetch para solicitudes de Whitelist
        const whitelistRes = await fetch(`${API_BASE_URL}/whitelistRequests`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!whitelistRes.ok) {
          throw new Error(`HTTP error! status: ${whitelistRes.status} al cargar whitelist`);
        }
        const whitelistData = await whitelistRes.json();

        // Cambia 'estado' por 'status' para el conteo correcto
        const pending = whitelistData.filter(req => req.status === 'pending').length;
        const approved = whitelistData.filter(req => req.status === 'approved').length;
        const suspended = whitelistData.filter(req => req.status === 'rejected').length;

        // 2. Fetch para el total de Usuarios
        const usersRes = await fetch(`${API_BASE_URL}/users`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!usersRes.ok) {
          throw new Error(`HTTP error! status: ${usersRes.status} al cargar usuarios`);
        }
        const usersData = await usersRes.json();
        const registeredUsers = usersData.length;

        // 3. Fetch para el total de Roles
        const rolesRes = await fetch(`${API_BASE_URL}/roles`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!rolesRes.ok) {
          throw new Error(`HTTP error! status: ${rolesRes.status} al cargar roles`);
        }
        const rolesData = await rolesRes.json();
        const definedRoles = rolesData.length;

        // 4. Fetch para el estado del servidor FiveM
        try {
          const fivemRes = await fetch(`${API_BASE_URL}/get_player_count`);
          if (fivemRes.ok) {
            const fivemData = await fivemRes.json();
            if (fivemData.status === 'success') {
              setServerStatus({
                players: fivemData.players,
                maxPlayers: fivemData.maxPlayers,
                fivem_maintenance: fivemData.fivem_maintenance
              });
            }
          }
        } catch (fivemErr) {
          console.warn('Error fetching FiveM server status:', fivemErr.message);
        } finally {
          setServerStatusLoading(false);
        }

        setData({
          pendingWhitelist: pending,
          approvedWhitelist: approved,
          suspendedWhitelist: suspended,
          registeredUsers: registeredUsers,
          definedRoles: definedRoles,
        });
      } catch (err) {
        setError(`Error al cargar los datos del dashboard: ${err.message}`);
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }

    };

    fetchDashboardData();
  }, [authToken]);

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <AdminSidebar />
        <div className="admin-content">
          <div className="dashboard-loading">
            <div className="spinner"></div>
            <p>Cargando datos del panel de administración...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-container">
        <AdminSidebar />
        <div className="admin-content">
          <div className="dashboard-error">
            <h2 className="admin-page-title">Panel de Administración</h2>
            <div className="error-card">
              <p className="error-message">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <AdminSidebar isUnderMaintenance={isUnderMaintenance} toggleMaintenanceMode={toggleMaintenanceMode} />
      <div className="admin-content">
        
        {/* Cabecera del Dashboard */}
        <div className="dashboard-header-modern">
          <div className="header-greeting">
            <h1 className="dashboard-title">Panel de Control</h1>
            <p className="dashboard-subtitle">Monitoreo en tiempo real y administración general de ProyectoRP</p>
          </div>
          <div className="header-status-badge">
            <span className={`dashboard-status-dot ${serverStatus.fivem_maintenance ? 'maintenance' : 'online'}`}></span>
            <span className="status-text">
              {serverStatus.fivem_maintenance ? 'Servidor en Mantenimiento' : 'Servidor Activo'}
            </span>
          </div>
        </div>

        {data && (
          <div className="dashboard-grid-modern">
            {/* Widget: Estado de FiveM */}
            <div className="dashboard-card-modern server-status-card">
              <div className="card-top-row">
                <div className="icon-wrapper server-icon">
                  <FaServer />
                </div>
                <span className="card-tag">FiveM</span>
              </div>
              <div className="card-body-row">
                <h3 className="metric-label">Jugadores Activos</h3>
                <h2 className="metric-value">
                  {serverStatusLoading ? '...' : `${serverStatus.players} / ${serverStatus.maxPlayers}`}
                </h2>
              </div>
              <div className="card-footer-row">
                <span className="footer-status-text">
                  {serverStatus.fivem_maintenance ? '🔧 Tareas de soporte' : '🟢 Latencia óptima'}
                </span>
              </div>
            </div>

            {/* Widget: Whitelist Pendientes */}
            <div className="dashboard-card-modern pending-card">
              <div className="card-top-row">
                <div className="icon-wrapper pending-icon">
                  <FaClock />
                </div>
                <span className="card-tag tag-pending">Pendiente</span>
              </div>
              <div className="card-body-row">
                <h3 className="metric-label">Solicitudes Whitelist</h3>
                <h2 className="metric-value">{data.pendingWhitelist}</h2>
              </div>
              <div className="card-footer-row">
                <span className="footer-subtext">Por revisar y aprobar</span>
              </div>
            </div>

            {/* Widget: Whitelist Aprobadas */}
            <div className="dashboard-card-modern approved-card">
              <div className="card-top-row">
                <div className="icon-wrapper approved-icon">
                  <FaCheckCircle />
                </div>
                <span className="card-tag tag-approved">Aprobado</span>
              </div>
              <div className="card-body-row">
                <h3 className="metric-label">Whitelist Aceptadas</h3>
                <h2 className="metric-value">{data.approvedWhitelist}</h2>
              </div>
              <div className="card-footer-row">
                <span className="footer-subtext">Jugadores registrados</span>
              </div>
            </div>

            {/* Widget: Whitelist Rechazadas */}
            <div className="dashboard-card-modern rejected-card">
              <div className="card-top-row">
                <div className="icon-wrapper rejected-icon">
                  <FaTimesCircle />
                </div>
                <span className="card-tag tag-rejected">Rechazado</span>
              </div>
              <div className="card-body-row">
                <h3 className="metric-label">Solicitudes Rechazadas</h3>
                <h2 className="metric-value">{data.suspendedWhitelist}</h2>
              </div>
              <div className="card-footer-row">
                <span className="footer-subtext">No admitidos</span>
              </div>
            </div>

            {/* Widget: Usuarios Registrados */}
            <div className="dashboard-card-modern users-card">
              <div className="card-top-row">
                <div className="icon-wrapper users-icon">
                  <FaUsers />
                </div>
                <span className="card-tag tag-users">Comunidad</span>
              </div>
              <div className="card-body-row">
                <h3 className="metric-label">Usuarios Web</h3>
                <h2 className="metric-value">{data.registeredUsers}</h2>
              </div>
              <div className="card-footer-row">
                <span className="footer-subtext">Cuentas registradas en la web</span>
              </div>
            </div>

            {/* Widget: Roles Definidos */}
            <div className="dashboard-card-modern roles-card">
              <div className="card-top-row">
                <div className="icon-wrapper roles-icon">
                  <FaUserShield />
                </div>
                <span className="card-tag tag-roles">Permisos</span>
              </div>
              <div className="card-body-row">
                <h3 className="metric-label">Roles Asignados</h3>
                <h2 className="metric-value">{data.definedRoles}</h2>
              </div>
              <div className="card-footer-row">
                <span className="footer-subtext">Estructura jerárquica activa</span>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;